import React, { useState, useEffect, useCallback } from 'react';
import { messagingAPI, optionsAPI, fiberAPI, customerAPI } from '../../services/api';
import { Hash, Tv, Wifi, Wallet, CalendarX } from 'lucide-react';

// Recipient picker shared by Message Center → Send and Kapso WhatsApp → Send to Customers,
// so both pick customers exactly the same way. The audience shape is resolved server-side
// by routes/messaging.js#resolveRecipients (recipients-preview / send / Kapso send-bulk).

const fmtINR = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Scrollable table of the actual matching customers — used by the audience types that
// have no further narrowing (pending payment / expired). Each row has a checkbox so the
// admin can prune the exact list before sending, rather than being locked into "everyone
// who matches"; selectedIds/onToggle/onToggleAll live in useRecipients so the ticked set
// can be turned into the real send-time audience (a customerIds list).
function ListPreviewBox({ hint, rows, columns, renderRow, selectedIds, onToggle, onToggleAll }) {
  const allChecked = rows?.length > 0 && selectedIds.size === rows.length;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: '.83rem', color: 'var(--text-muted)' }}>
        {hint}
      </div>
      {!rows ? (
        <div style={{ padding: '16px 14px', color: 'var(--text-muted)', fontSize: '.85rem' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '16px 14px', color: 'var(--text-muted)', fontSize: '.85rem' }}>No matching customers</div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--card-bg)' }}>
                <th style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox" checked={allChecked} onChange={onToggleAll} />
                </th>
                {columns.map(c => (
                  <th key={c} style={{ textAlign: 'left', padding: '6px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 600 }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r._id || i} style={{ borderBottom: '1px solid var(--border)', opacity: selectedIds.has(r._id) ? 1 : 0.45 }}>
                  <td style={{ padding: '6px 12px' }}>
                    <input type="checkbox" checked={selectedIds.has(r._id)} onChange={() => onToggle(r._id)} />
                  </td>
                  {renderRow(r).map((v, j) => (
                    <td key={j} style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Audience types whose picker is a full list-with-checkboxes (Pending Payment / Expired)
// rather than a node/ISP narrowing form.
const LIST_AUD_TYPES = ['catv_pending', 'catv_expired', 'broadband_expired'];

// All picker state + the live recipient preview. Returns what the caller needs to send:
// sendAudience() (the audience to POST) and recipientCount (what the Send button shows).
export function useRecipients() {
  const [audType, setAudType] = useState('catv'); // catv | broadband | catv_pending | catv_expired | broadband_expired | numbers
  const [nodes, setNodes] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]); // empty = all cable customers
  const [catvType, setCatvType] = useState(''); // '' = all | 'regular' | 'dtd'
  const [catvActive, setCatvActive] = useState(''); // '' = all | 'active' | 'inactive'
  const [isps, setIsps] = useState([]);
  const [isp, setIsp] = useState(''); // '' = all ISPs
  const [ponPorts, setPonPorts] = useState([]);
  const [selectedPons, setSelectedPons] = useState([]); // empty = all PON ports
  const [numbersText, setNumbersText] = useState('');
  const [preview, setPreview] = useState(null);
  // Which rows are ticked in a LIST_AUD_TYPES table — reset to "everyone" whenever a fresh
  // full list arrives (switching audience type, or its underlying data changing).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const isListType = LIST_AUD_TYPES.includes(audType);

  useEffect(() => {
    // Real node values typed onto customer records (catv.node) — not the curated
    // AppOptions "nodes" list, which can drift out of sync with what's actually in use.
    customerAPI.getNodes().then(r => {
      setNodes(r.data?.data || []);
    }).catch(() => {});
    optionsAPI.get('ispProfiles').then(r => {
      const vals = r.data?.data || [];
      setIsps(vals.map(v => (typeof v === 'string' ? v : v.value || v.label || v)));
    }).catch(() => {});
    fiberAPI.getPONPorts().then(r => setPonPorts(r.data?.data || [])).catch(() => {});
  }, []);

  // Audience used to populate the picker (node list, ISP/PON form, or — for LIST_AUD_TYPES
  // — the full unfiltered matching-customer table). Checkbox selection is intentionally NOT
  // folded in here; sendAudience() below layers that on top so ticking/unticking rows never
  // re-triggers a server round-trip or reshuffles the table.
  const audience = useCallback(() => {
    if (audType === 'numbers') {
      return { type: 'numbers', numbers: numbersText.split(/[\s,;]+/).filter(Boolean) };
    }
    if (audType === 'catv') {
      return { type: 'catv', nodes: selectedNodes, catvType, catvActive };
    }
    if (audType === 'broadband') {
      return { type: 'broadband', isp, ponPorts: selectedPons };
    }
    // catv_pending / catv_expired / broadband_expired — full list, no further narrowing
    return { type: audType };
  }, [audType, selectedNodes, catvType, catvActive, isp, selectedPons, numbersText]);

  // Live recipient count / list preview
  useEffect(() => {
    const t = setTimeout(() => {
      messagingAPI.previewRecipients(audience())
        .then(r => setPreview(r.data?.data || null))
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [audience]);

  // Default every fresh list-preview fetch to "everyone ticked" — same recipients as
  // before checkboxes existed unless the admin deliberately prunes some out.
  useEffect(() => {
    if (isListType && preview?.sample) {
      setSelectedIds(new Set(preview.sample.map(c => c._id)));
    }
  }, [isListType, preview]);

  // The audience actually sent / counted at the bottom — for list types this narrows to
  // whichever rows are still ticked instead of the full matched set.
  const sendAudience = useCallback(
    () => (isListType ? { type: 'customerIds', ids: [...selectedIds] } : audience()),
    [isListType, selectedIds, audience]
  );
  const recipientCount = isListType ? selectedIds.size : (preview?.count || 0);

  return {
    audType, setAudType, nodes, selectedNodes, setSelectedNodes, catvType, setCatvType, catvActive, setCatvActive,
    isps, isp, setIsp, ponPorts, selectedPons, setSelectedPons, numbersText, setNumbersText,
    preview, selectedIds, setSelectedIds, isListType, sendAudience, recipientCount,
  };
}

// The Recipients form group: audience buttons + the narrowing form / list for the chosen type.
export function RecipientPicker({ r }) {
  const { audType, setAudType, nodes, selectedNodes, setSelectedNodes, catvType, setCatvType, catvActive, setCatvActive,
    isps, isp, setIsp, ponPorts, selectedPons, setSelectedPons, numbersText, setNumbersText, preview, selectedIds, setSelectedIds } = r;

  const toggleId = (id) => setSelectedIds(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllIds = () => setSelectedIds(s =>
    s.size === (preview?.sample?.length || 0) ? new Set() : new Set((preview?.sample || []).map(c => c._id))
  );
  const toggleNode = (n) => setSelectedNodes(s => s.includes(n) ? s.filter(x => x !== n) : [...s, n]);
  const togglePon  = (p) => setSelectedPons(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);

  const chipStyle = (active) => ({
    fontSize: '.78rem', padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    background: active ? 'var(--primary)' : 'var(--card-bg)',
    color: active ? 'var(--c-ffffff)' : 'var(--text)',
  });

  return (
    <div className="form-group">
      <label className="form-label">Recipients</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { key: 'catv', label: 'CableTV', icon: Tv },
          { key: 'broadband', label: 'Broadband', icon: Wifi },
          { key: 'catv_pending', label: 'CableTV — Pending Payment', icon: Wallet },
          { key: 'catv_expired', label: 'Expired — CableTV', icon: CalendarX },
          { key: 'broadband_expired', label: 'Expired — Broadband', icon: CalendarX },
          { key: 'numbers', label: 'Custom Numbers', icon: Hash },
        ].map(a => (
          <button key={a.key} type="button" onClick={() => setAudType(a.key)}
            className={`btn btn-sm ${audType === a.key ? 'btn-primary' : 'btn-secondary'}`}>
            <a.icon size={13} style={{ marginRight: 5 }} />{a.label}
          </button>
        ))}
      </div>

      {/* CableTV: all customers or specific nodes (checkbox list, same pattern as Agent Area) */}
      {audType === 'catv' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Billing Type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={() => setCatvType('')} style={chipStyle(catvType === '')}>All</button>
              <button type="button" onClick={() => setCatvType('regular')} style={chipStyle(catvType === 'regular')}>Regular</button>
              <button type="button" onClick={() => setCatvType('dtd')} style={chipStyle(catvType === 'dtd')}>DTD</button>
              <button type="button" onClick={() => setCatvType('amp')} style={chipStyle(catvType === 'amp')}>AMP</button>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Connection Status</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={() => setCatvActive('')} style={chipStyle(catvActive === '')}>All</button>
              <button type="button" onClick={() => setCatvActive('active')} style={chipStyle(catvActive === 'active')}>Active</button>
              <button type="button" onClick={() => setCatvActive('inactive')} style={chipStyle(catvActive === 'inactive')}>Inactive</button>
            </div>
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.85rem', fontWeight: 600 }}>
                <input type="checkbox"
                  checked={selectedNodes.length === nodes.length && nodes.length > 0}
                  onChange={() => setSelectedNodes(selectedNodes.length === nodes.length ? [] : [...nodes])}
                />
                All Customers (select all nodes)
              </label>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {nodes.length === 0 ? (
                <div style={{ padding: '16px 14px', color: 'var(--text-muted)', fontSize: '.85rem' }}>No nodes defined yet</div>
              ) : (
                nodes.map(n => (
                  <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', cursor: 'pointer', fontSize: '.83rem', borderBottom: '1px solid var(--border)' }}>
                    <input type="checkbox" checked={selectedNodes.includes(n)} onChange={() => toggleNode(n)} />
                    {n}
                  </label>
                ))
              )}
            </div>
            <div style={{ padding: '6px 14px', fontSize: '.74rem', color: 'var(--text-muted)', background: 'var(--bg)' }}>
              {selectedNodes.length === 0 ? 'No nodes checked = every cable customer gets this message.' : `${selectedNodes.length} node${selectedNodes.length !== 1 ? 's' : ''} selected.`}
            </div>
          </div>
        </div>
      )}

      {/* Broadband: ISP + PON ports (fiber mapping) */}
      {audType === 'broadband' && (
        <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>ISP</div>
            <select className="form-control" style={{ maxWidth: 260 }} value={isp} onChange={e => setIsp(e.target.value)}>
              <option value="">All ISPs</option>
              {isps.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>
              PON Port — matched through fiber mapping; leave all off for <b>every PON</b>:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={() => setSelectedPons([])} style={chipStyle(selectedPons.length === 0)}>
                All PON Ports
              </button>
              {ponPorts.map(p => (
                <button key={p.port} type="button" onClick={() => togglePon(p.port)} style={chipStyle(selectedPons.includes(p.port))}
                  title={`${p.onuCount} ONU mapping(s) on this port`}>
                  PON {p.port}{p.name ? ` · ${p.name}` : ''} ({p.onuCount})
                </button>
              ))}
              {ponPorts.length === 0 && <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>No PON ports configured — add them in Fiber Map</span>}
            </div>
            {selectedPons.length > 0 && (
              <div style={{ fontSize: '.74rem', color: 'var(--c-92400e)', marginTop: 6 }}>
                Only customers linked to an ONU on the selected PON(s) in Fiber Map will match — unmapped customers are not included.
              </div>
            )}
          </div>
        </div>
      )}

      {audType === 'numbers' && (
        <textarea className="form-control font-mono" rows={3} value={numbersText}
          onChange={e => setNumbersText(e.target.value)}
          placeholder="9876543210, 9825012345 — comma, space or newline separated" />
      )}

      {audType === 'catv_pending' && (
        <ListPreviewBox
          hint="Every Active CableTV customer with a due balance (outstanding balance > 0), regardless of node — suspended and inactive customers are excluded. Untick anyone you don't want to message."
          rows={preview?.sample}
          columns={['Bill No', 'Name', 'Mobile', 'Area', 'Due']}
          renderRow={c => [c.billingNumber ?? '—', c.name, c.mobile, c.address?.area || '—', `₹${fmtINR(c.outstandingBalance)}`]}
          selectedIds={selectedIds} onToggle={toggleId} onToggleAll={toggleAllIds}
        />
      )}
      {audType === 'catv_expired' && (
        <ListPreviewBox
          hint="Every CableTV customer whose subscription expiry date has already passed. Untick anyone you don't want to message."
          rows={preview?.sample}
          columns={['Bill No', 'Name', 'Mobile', 'Area', 'Expired On']}
          renderRow={c => [c.billingNumber ?? '—', c.name, c.mobile, c.address?.area || '—', fmtDate(c.catv?.expiryDate)]}
          selectedIds={selectedIds} onToggle={toggleId} onToggleAll={toggleAllIds}
        />
      )}
      {audType === 'broadband_expired' && (
        <ListPreviewBox
          hint="Every Broadband customer whose plan expiry date has already passed. Untick anyone you don't want to message."
          rows={preview?.sample}
          columns={['Bill No', 'Name', 'Mobile', 'Username', 'Area', 'Expired On']}
          renderRow={c => [c.billingNumber ?? '—', c.name, c.mobile, c.broadband?.username || '—', c.address?.area || '—', fmtDate(c.broadband?.expiryDate)]}
          selectedIds={selectedIds} onToggle={toggleId} onToggleAll={toggleAllIds}
        />
      )}
    </div>
  );
}

// "N recipients will get this message — e.g. …" line next to the Send button.
export function RecipientCount({ r }) {
  const { isListType, recipientCount, preview } = r;
  return (
    <div style={{ fontSize: '.86rem' }}>
      {isListType
        ? <><b>{recipientCount}</b> of {preview?.sample?.length ?? 0} ticked will get this message</>
        : preview
          ? <><b>{preview.count}</b> recipient{preview.count !== 1 ? 's' : ''} will get this message
              {preview.sample?.length > 0 && (
                <span style={{ color: 'var(--text-muted)' }}> — e.g. {preview.sample.map(s => s.name).slice(0, 3).join(', ')}…</span>
              )}</>
          : <span style={{ color: 'var(--text-muted)' }}>Select recipients to see the count</span>}
    </div>
  );
}
